import { NetworkStatus } from '@apollo/client';
import { Label, LabelAssignment } from '@provision/entity-types';
import {
  gql,
  QueryHookOptions,
  Reference,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish, uniqueBy } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { GetLabelGroups, useLabelGroups } from 'src/graphql/entities/label-group';
import { META_DATA_QUERY_FIELDS } from 'src/graphql/entities/metadata.fragments';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';
import { LABEL_FIELDS } from './label.fragments';

/** Page size for SelectMenu label pickers (server-backed incremental load). */
export const LABEL_PICKER_PAGE_SIZE = 50;

export type LabelMap = Record<string, Label[]>;

export interface LabelOptionValue {
  label: string;
  id: string;
  value: string | number;
  type: string;
  group?: LabelOptionValue;
  isPublic?: boolean;
  selected?: boolean;
  color?: string;
  partnerId?: number;
}

export const onlyPublicsFilter = {
  attribute: 'isPublic',
  operator: '=',
  value: 'true',
};

export interface LabelsFilterParams {
  partnerId?: number | null;
  groupId?: string;
  includeOnlyOutsideGroups?: boolean;
  extraFilters?: ConditionInput[];
}

export const buildLabelsFilters = ({
  partnerId,
  groupId,
  includeOnlyOutsideGroups,
  extraFilters = [],
}: LabelsFilterParams): ConditionInput[] => {
  const filters: ConditionInput[] = [];

  if (!isNullish(partnerId)) {
    filters.push(
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: 'in',
        value: [partnerId, PLATFORM_PARTNER_ID].join(','),
      },
    );
  }

  if (groupId) {
    filters.push({
      attribute: 'labelGroupId',
      operator: '=',
      value: groupId,
    });
  }

  if (includeOnlyOutsideGroups) {
    filters.push({
      attribute: 'labelGroupId',
      value: '',
      operator: 'is_null',
    });
  }

  return [...filters, ...extraFilters];
};

const GET_LABELS_QUERY = gql`
  query GetLabel($params: QueryParams, $skipTotalRecords: Boolean = false)
  @entity(name: "Label") {
    getLabel(queryParams: $params) {
      records {
        ...LabelFields
      }
      metadata {
        ...MetaDataQueryFields
      }
    }
  }
  ${LABEL_FIELDS}
  ${META_DATA_QUERY_FIELDS}
`;

export const CREATE_LABEL_MUTATION = gql`
  mutation CreateLabel($data: LabelDataInput) {
    createLabel(data: $data) {
      ...LabelFields
    }
  }
  ${LABEL_FIELDS}
`;

const UPDATE_LABEL_MUTATION = gql`
  mutation UpdateLabel($conditions: [ConditionInput], $data: LabelDataInput) {
    updateLabel(conditions: $conditions, data: $data) {
      ...LabelFields
    }
  }
  ${LABEL_FIELDS}
`;

const DELETE_LABEL_MUTATION = gql`
  mutation DeleteLabelWithRelatedData($labelId: String!) {
    deleteLabelWithRelatedData(params: { labelId: $labelId }) {
      labelId
    }
  }
`;

export const useLabel = (
  options?: QueryHookOptions<GetLabels> & {
    desc?: boolean;
    filters?: ConditionInput[];
  },
) => {
  const { desc = true, filters = [] } = options ?? {};

  const { showErrorAlert } = useNotifications();
  const [updateLabelMutation] = useMutation<{
    updateLabel: Label[];
  }>(UPDATE_LABEL_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong updating the label, please try again');
    },
  });

  const updateLabel = useCallback(
    (label: Label, oldGroupId?: string | null) => {
      if (!label.labelId) {
        return;
      }

      const patch = {
        displayName: label.displayName,
        isPublic: label.isPublic,
        labelGroupId: label.labelGroupId,
      };

      return updateLabelMutation({
        variables: {
          conditions: [
            {
              attribute: 'labelId',
              operator: '=',
              value: label.labelId,
            },
          ],
          data: patch,
        },
        optimisticResponse: {
          updateLabel: [
            {
              ...label,
              updatedAt: new Date().toISOString(),
              ...patch,
            },
          ],
        },
        update(cache, { data }) {
          if (!data?.updateLabel?.length) return;
          if (!data.updateLabel[0].labelId) return;

          cache.updateQuery<GetLabels, QueryVariables>(
            {
              query: GET_LABELS_QUERY,
              variables: {
                skipTotalRecords: false,
                params: {
                  filters,
                  sorting: { attribute: 'updatedAt', desc },
                },
              },
            },
            (existingData) => {
              if (!existingData) return;

              let records = existingData.getLabel.records;

              // If oldGroupId exists and current labelGroupId is null is because
              // it was moved from a group to non group
              if (label.labelGroupId === null && oldGroupId) {
                records = [
                  {
                    ...data?.updateLabel[0],
                    ...label,
                  },
                  ...records,
                ];
              } else if (label.labelGroupId !== oldGroupId) {
                records = records.filter(
                  (label) => label.labelId !== data?.updateLabel[0].labelId,
                );
              }

              return {
                getLabel: {
                  ...existingData.getLabel,
                  records,
                },
              };
            },
          );

          if (label.labelGroupId && label.labelGroupId !== oldGroupId) {
            const id = cache.identify({
              __typename: 'LabelGroup',
              id: label.labelGroupId,
            });

            cache.modify({
              id,
              fields: {
                labels(existingItems = [], { toReference }) {
                  const existingLabels = existingItems as Reference[];
                  return [
                    toReference({ ...data?.updateLabel[0], ...label }),
                    ...existingLabels,
                  ];
                },
              },
            });
          }

          if (oldGroupId && oldGroupId !== label.labelGroupId) {
            const id = cache.identify({
              __typename: 'LabelGroup',
              id: oldGroupId,
            });

            cache.modify({
              id,
              fields: {
                labels(existingItems = [], { readField }) {
                  const existingLabels = existingItems as Reference[];
                  return existingLabels.filter((l) => readField('id', l) !== label.labelId);
                },
              },
            });
          }
        },
      });
    },
    [desc, filters, updateLabelMutation],
  );

  const [deleteLabelMutation] = useMutation<{
    deleteLabelWithRelatedData: {
      labelId: string;
    };
  }>(DELETE_LABEL_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong deleting the label, please try again');
    },
  });

  const deleteLabel = useCallback(
    (label: Label) => {
      return deleteLabelMutation({
        variables: { labelId: label.labelId },
        optimisticResponse: {
          deleteLabelWithRelatedData: {
            labelId: label.labelId,
          },
        },
        update(cache, { data }) {
          if (!data?.deleteLabelWithRelatedData?.labelId) return;

          const id = cache.identify({
            __typename: 'Label',
            id: label.labelId,
          });

          cache.evict({ id });

          if (label.labelGroupId) {
            const id = cache.identify({
              __typename: 'LabelGroup',
              id: label.labelGroupId,
            });

            cache.modify({
              id,
              fields: {
                labels(existingItems = [], { readField }) {
                  const existingLabels = existingItems as Reference[];
                  return existingLabels.filter((l) => readField('id', l) !== label.labelId);
                },
              },
            });
          }

          cache.gc();
        },
      });
    },
    [deleteLabelMutation],
  );

  const [createLabelMutation] = useMutation<{
    createLabel: Label[];
  }>(CREATE_LABEL_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong creating a new label, please try again');
    },
  });

  const createLabel = useCallback(
    async (
      label: Partial<Omit<Label, 'labelGroupId'>> & {
        labelGroupId?: null | string;
      },
      partnerId?: number,
    ) => {
      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createLabelMutation({
        variables: {
          data: {
            partnerId,
            displayName: label.displayName,
            labelGroupId: label.labelGroupId,
          },
        },
        optimisticResponse: {
          createLabel: [
            {
              id: label.labelId || tempId,
              labelId: label.labelId || tempId,
              labelGroupId: label.labelGroupId ?? '',
              displayName: label.displayName ?? '',
              isPublic: label.isPublic || true,
              partnerId: partnerId ?? label.partnerId!,
              createdAt,
              updatedAt: createdAt,
              labelGroup: label.labelGroup!,
              partner: label.partner!,
              labelAssociations: [],
              labelAssignments: [],
            },
          ],
        },
        update(cache, { data }) {
          if (!data?.createLabel[0].labelId) return;
          const createdLabel = data.createLabel[0];
          const isTempLabel = createdLabel.labelId.startsWith('temp-id');
          const normalizedGroupId = createdLabel.labelGroupId || null;

          cache.updateQuery<GetLabels, QueryVariables>(
            {
              query: GET_LABELS_QUERY,
              variables: {
                skipTotalRecords: false,
                params: {
                  filters,
                  sorting: { attribute: 'updatedAt', desc },
                },
              },
            },
            (existingData) => {
              if (!existingData) return;

              const filteredRecords = existingData.getLabel.records.filter((record) => {
                if (record.labelId === createdLabel.labelId) {
                  return false;
                }

                if (!isTempLabel && record.labelId?.startsWith('temp-id')) {
                  return !(
                    record.displayName === createdLabel.displayName &&
                    record.partnerId === createdLabel.partnerId &&
                    (record.labelGroupId ?? null) === normalizedGroupId
                  );
                }

                return true;
              });

              return {
                getLabel: {
                  ...existingData.getLabel,
                  records: [
                    {
                      ...createdLabel,
                      labelGroupId: normalizedGroupId,
                    } as Label,
                    ...filteredRecords,
                  ],
                },
              };
            },
          );

          if (label.labelGroupId) {
            const id = cache.identify({
              __typename: 'LabelGroup',
              id: label.labelGroupId,
            });

            cache.modify({
              id,
              fields: {
                labels(existingItems = [], { toReference, readField }) {
                  const existingLabels = existingItems as Reference[];
                  const createdRef = toReference({ ...createdLabel, ...label });
                  const filteredLabels = existingLabels.filter((existingLabel) => {
                    const existingId = readField('labelId', existingLabel);
                    if (existingId === createdLabel.labelId) {
                      return false;
                    }

                    if (
                      !isTempLabel &&
                      typeof existingId === 'string' &&
                      existingId.startsWith('temp-id')
                    ) {
                      const displayName = readField('displayName', existingLabel);
                      const partnerId = readField('partnerId', existingLabel);
                      const labelGroupId = readField('labelGroupId', existingLabel);

                      return !(
                        displayName === createdLabel.displayName &&
                        partnerId === createdLabel.partnerId &&
                        (labelGroupId ?? null) === normalizedGroupId
                      );
                    }

                    return true;
                  });

                  return [createdRef, ...filteredLabels];
                },
              },
            });
          }
        },
      });

      return data?.createLabel;
    },
    [createLabelMutation, desc, filters],
  );

  return {
    updateLabel,
    createLabel,
    deleteLabel,
  };
};

// `type` (not `interface`) so this stays assignable to useLoadMore's Record<> constraint.
type GetLabels = {
  getLabel: Records<Label>;
};

export const useLabels = (
  params: {
    partnerId?: number | null;
    groupId?: string;
    includeOnlyOutsideGroups?: boolean;
  },
  options?: QueryHookOptions<GetLabels> & {
    desc?: boolean;
    filters?: ConditionInput[];
    /** When set, pages results and exposes `loadMore` / `hasMore` for SelectMenu. */
    limit?: number;
  },
) => {
  const { partnerId, groupId, includeOnlyOutsideGroups } = params;

  const {
    desc = true,
    filters: extraFilters = [],
    skip = false,
    limit,
    ...queryOptions
  } = options ?? {};
  const filters = buildLabelsFilters({
    partnerId,
    groupId,
    includeOnlyOutsideGroups,
    extraFilters,
  });

  const { data, fetchMore, variables, networkStatus, ...rest } = useQuery<
    GetLabels,
    QueryVariables
  >(GET_LABELS_QUERY, {
    ...queryOptions,
    context: {
      includePlatformLevelData: true,
      ...queryOptions.context,
    },
    skip,
    notifyOnNetworkStatusChange:
      limit !== undefined || queryOptions.notifyOnNetworkStatusChange,
    variables: {
      skipTotalRecords: false,
      params: {
        filters,
        sorting: { attribute: 'updatedAt', desc },
        ...(limit !== undefined ? { limit } : {}),
      },
    },
  });

  const labels = useMemo(
    () => uniqueBy(data?.getLabel.records || [], (label) => label.labelId),
    [data?.getLabel.records],
  );

  const { loadMore, hasReachedEnd } = useLoadMore<GetLabels>({
    offset: labels.length,
    fetchMore,
    params: variables?.params!,
    deduplicationPredicate: (left, right) => left.id === right.id,
  });

  const totalRecords = data?.getLabel?.metadata?.totalRecords ?? 0;
  // When the API omits totalRecords (common in the local AppSync simulator), a short
  // final page means we're done — otherwise load-more can re-fetch the same rows and
  // duplicate them in the picker.
  let hasMore = false;
  if (limit !== undefined) {
    hasMore = totalRecords
      ? labels.length < totalRecords
      : labels.length >= limit && !hasReachedEnd;
  }

  const operations = useLabel({ filters });

  return {
    labels,
    loadMore,
    hasMore,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    ...operations,
    ...rest,
  };
};

export function useLabelsOptions({
  partnerId,
  labelGroupsOptions,
  labelsFilters = [],
  labelAssignments,
  selectedLabelGroup,
  skip,
}: {
  partnerId?: number | null;
  labelGroupsOptions?: QueryHookOptions<GetLabelGroups> & {
    desc?: boolean;
    filters?: ConditionInput[];
  };
  labelsFilters?: ConditionInput[];
  labelAssignments?: Pick<LabelAssignment, 'labelId'>[];
  selectedLabelGroup?: LabelOptionValue;
  skip?: boolean;
}) {
  const { labels, loading } = useLabels({ partnerId }, { filters: labelsFilters, skip });

  const { labelGroups } = useLabelGroups({ partnerId }, labelGroupsOptions);

  const labelOptions = useMemo(
    () =>
      [
        ...labelGroups.map(
          (group) =>
            ({
              id: group.id,
              label: group.displayName ?? '',
              type: 'group',
              partnerId: group.partnerId,
            }) as LabelOptionValue,
        ),
        ...labels.map((label) => {
          return {
            ...label,
            id: label.labelId,
            label: label.displayName ?? '',
            value: label.displayName,
            type: 'label',
            group: selectedLabelGroup,
            isPublic: label.isPublic,
            selected: labelAssignments?.some(
              (labelAssignment) => labelAssignment.labelId === label.labelId,
            ),
          } as LabelOptionValue;
        }),
      ].map((i) => ({ ...i, value: i.id })),
    [labelGroups, labels, selectedLabelGroup, labelAssignments],
  );

  return { labelOptions, loading };
}
